import { getRangeLength } from "@teselagen/range-utils";
import { map, cloneDeep } from "lodash-es";
import convertDnaCaretPositionOrRangeToAa from "./convertDnaCaretPositionOrRangeToAA";
import rotateSequenceDataToPosition from "./rotateSequenceDataToPosition";
import { adjustRangeToDeletionOfAnotherRange } from "@teselagen/range-utils";
import tidyUpSequenceData from "./tidyUpSequenceData";
import { modifiableTypes } from "./annotationTypes";
import adjustBpsToReplaceOrInsert from "./adjustBpsToReplaceOrInsert";
import adjustAnnotationsToInsert from "./adjustAnnotationsToInsert";
export default function insertSequenceDataAtPositionOrRange(_sequenceDataToInsert, _existingSequenceData, caretPositionOrRange, options = {}) {
  const { maintainOriginSplit } = options;
  let existingSequenceData = tidyUpSequenceData(_existingSequenceData, {
    doNotRemoveInvalidChars: true,
    ...options
  });
  const sequenceDataToInsert = tidyUpSequenceData(_sequenceDataToInsert, {
    topLevelSeqData: existingSequenceData,
    ...options
  });
  const newSequenceData = cloneDeep(existingSequenceData);
  const insertLength = sequenceDataToInsert.isProtein && sequenceDataToInsert.proteinSequence ? sequenceDataToInsert.proteinSequence.length * 3 : sequenceDataToInsert.sequence.length;
  let caretPosition = caretPositionOrRange;
  const isInsertSameLengthAsSelection = sequenceDataToInsert.sequence.length === getRangeLength(caretPositionOrRange, existingSequenceData.sequence.length);
  if (caretPositionOrRange.start > -1 && getRangeLength(
    caretPositionOrRange,
    existingSequenceData.sequence.length
  ) === existingSequenceData.sequence.length) {
    existingSequenceData = tidyUpSequenceData(
      {
        ...existingSequenceData,
        ...modifiableTypes.reduce((acc, type) => {
          return acc[type] = [];
        }, {}),
        sequence: "",
        doNotRemoveInvalidChars: true,
        proteinSequence: "",
        chromatogramData: void 0
      },
      options
    );
    newSequenceData.chromatogramData = void 0;
  } else if (newSequenceData.chromatogramData && newSequenceData.chromatogramData.baseTraces) {
    if (caretPositionOrRange && caretPositionOrRange.start > -1) {
      if (caretPositionOrRange.start > caretPositionOrRange.end) {
        newSequenceData.chromatogramData = trimChromatogram({
          chromatogramData: newSequenceData.chromatogramData,
          range: {
            start: caretPositionOrRange.start,
            end: newSequenceData.sequence.length
          },
          justBaseCalls: isInsertSameLengthAsSelection
        });
        newSequenceData.chromatogramData = trimChromatogram({
          chromatogramData: newSequenceData.chromatogramData,
          range: {
            start: 0,
            end: caretPositionOrRange.end
          },
          justBaseCalls: isInsertSameLengthAsSelection
        });
      } else {
        newSequenceData.chromatogramData = trimChromatogram({
          chromatogramData: newSequenceData.chromatogramData,
          range: {
            start: caretPositionOrRange.start,
            end: caretPositionOrRange.end
          },
          justBaseCalls: isInsertSameLengthAsSelection
        });
      }
    }
    if (sequenceDataToInsert.sequence) {
      insertIntoChromatogram({
        chromatogramData: newSequenceData.chromatogramData,
        caretPosition: caretPositionOrRange.start > -1 ? caretPositionOrRange.start : caretPositionOrRange,
        seqToInsert: sequenceDataToInsert.sequence,
        justBaseCalls: isInsertSameLengthAsSelection
      });
    }
  }
  newSequenceData.sequence = adjustBpsToReplaceOrInsert(
    existingSequenceData.sequence,
    sequenceDataToInsert.sequence,
    caretPositionOrRange
  );
  newSequenceData.size = newSequenceData.sequence.length;
  newSequenceData.proteinSequence = adjustBpsToReplaceOrInsert(
    existingSequenceData.proteinSequence,
    sequenceDataToInsert.proteinSequence,
    convertDnaCaretPositionOrRangeToAa(caretPositionOrRange)
  );
  newSequenceData.proteinSize = newSequenceData.proteinSequence.length;
  modifiableTypes.forEach((annotationType) => {
    let existingAnnotations = existingSequenceData[annotationType];
    if (caretPositionOrRange && caretPositionOrRange.start > -1) {
      const range = caretPositionOrRange;
      caretPosition = range.start > range.end ? 0 : range.start;
      existingAnnotations = adjustAnnotationsToDelete(
        existingAnnotations,
        range,
        existingSequenceData.sequence.length
      );
    }
    newSequenceData[annotationType] = [];
    newSequenceData[annotationType] = newSequenceData[annotationType].concat(
      adjustAnnotationsToInsert(
        existingAnnotations,
        caretPosition,
        insertLength
      )
    );
    newSequenceData[annotationType] = newSequenceData[annotationType].concat(
      adjustAnnotationsToInsert(
        sequenceDataToInsert[annotationType],
        0,
        caretPosition
      )
    );
  });
  if (maintainOriginSplit && caretPositionOrRange && caretPositionOrRange.start > caretPositionOrRange.end) {
    const caretPosToRotateTo = existingSequenceData.sequence.length - caretPositionOrRange.start;
    return rotateSequenceDataToPosition(
      newSequenceData,
      Math.min(caretPosToRotateTo, insertLength)
    );
  }
  return newSequenceData;
}
function adjustAnnotationsToDelete(annotationsToBeAdjusted, range, maxLength) {
  return map(annotationsToBeAdjusted, (annotation) => {
    const newRange = adjustRangeToDeletionOfAnotherRange(
      annotation,
      range,
      maxLength
    );
    const newLocations = annotation.locations && annotation.locations.map((loc) => adjustRangeToDeletionOfAnotherRange(loc, range, maxLength)).filter((range2) => !!range2);
    if (newLocations && newLocations.length) {
      return {
        ...newRange,
        start: newLocations[0].start,
        end: newLocations[newLocations.length - 1].end,
        ...newLocations.length > 0 && { locations: newLocations }
      };
    } else {
      return newRange;
    }
  }).filter((range2) => !!range2);
}
function insertIntoChromatogram({
  chromatogramData,
  caretPosition,
  seqToInsert,
  justBaseCalls
}) {
  if (!seqToInsert.length) return;
  chromatogramData.baseCalls && chromatogramData.baseCalls.splice(
    caretPosition,
    0,
    ...seqToInsert.split("")
  );
  if (justBaseCalls) {
    return chromatogramData;
  }
  const baseTracesToInsert = [];
  const qualNumsToInsert = [];
  for (let index = 0; index < seqToInsert.length; index++) {
    qualNumsToInsert.push(0);
    const toPush = [0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0];
    baseTracesToInsert.push({
      aTrace: toPush,
      cTrace: toPush,
      gTrace: toPush,
      tTrace: toPush
    });
  }
  chromatogramData.baseTraces && chromatogramData.baseTraces.splice(caretPosition, 0, ...baseTracesToInsert);
  chromatogramData.qualNums && chromatogramData.qualNums.splice(caretPosition, 0, ...qualNumsToInsert);
  return chromatogramData;
}
function trimChromatogram({
  chromatogramData,
  range: { start, end },
  justBaseCalls
}) {
  [
    "baseCalls",
    ...justBaseCalls ? [] : ["qualNums", "baseTraces", "basePos"]
  ].forEach((type) => {
    chromatogramData[type] && chromatogramData[type].splice(start, end - start + 1);
  });
  return chromatogramData;
}
